import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowCounterClockwise, Check, Moon, Sun, UploadSimple, Trash } from "@phosphor-icons/react";
import { AppHeader, photoSrc } from "@/components/AppHeader";
import { api, API } from "@/lib/api";
import {
  DEFAULT_APPEARANCE,
  DEFAULT_GOLD,
  GOLD_PRESETS,
  PRESETS,
  contrastRatio,
  FONT_OPTIONS,
  applyAccent,
  applyAppearance,
  applyGold,
  applyTheme,
  getAccent,
  getAppearance,
  getGold,
  getTheme,
  resetAppearance,
  resetGold,
} from "@/lib/theme";

const ACCENT_OPTIONS = [
  { key: "gold", label: "Brioni gold", hex: "#c39e6d" },
  { key: "indigo", label: "Indigo", hex: "#6264a0" },
  { key: "blue", label: "Blue", hex: "#2f6cbe" },
  { key: "sky", label: "Sky", hex: "#2b7fd0" },
  { key: "cyan", label: "Cyan", hex: "#1a8a99" },
  { key: "teal", label: "Teal", hex: "#1e7a7a" },
  { key: "emerald", label: "Emerald", hex: "#227a54" },
  { key: "lime", label: "Lime", hex: "#5a8a1e" },
  { key: "amber", label: "Amber", hex: "#a87828" },
  { key: "orange", label: "Orange", hex: "#c1651e" },
  { key: "copper", label: "Copper", hex: "#a5602e" },
  { key: "crimson", label: "Crimson", hex: "#b83232" },
  { key: "rose", label: "Rose", hex: "#b04260" },
  { key: "pink", label: "Pink", hex: "#c0497f" },
  { key: "fuchsia", label: "Fuchsia", hex: "#a83296" },
  { key: "violet", label: "Violet", hex: "#7c5cbe" },
  { key: "slate", label: "Slate", hex: "#58627c" },
];

const COLOR_FIELDS = [
  { key: "canvas", label: "Main background", hint: "The page canvas behind everything" },
  { key: "sidebar", label: "Side menu background", hint: "The left navigation & top bar" },
  { key: "panel", label: "Panel background", hint: "Cards and content panels" },
  { key: "ink", label: "Font color", hint: "Primary text color" },
  { key: "muted", label: "Secondary text", hint: "Labels, hints and captions — raise this if dark mode text is hard to read" },
];

const BrandLogoCard = () => {
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  useEffect(() => {
    api.get("/settings/branding").then(({ data }) => setLogo(data.header_logo || data.logo || null)).catch(() => {});
  }, []);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/settings/header-logo", fd);
      setLogo(data.header_logo);
      setBust(Date.now());
      toast.success("Header logo updated — reload to see it in the top bar");
    } catch {
      toast.error("Upload failed");
    }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/settings/header-logo");
      setLogo(null);
      setBust(Date.now());
      toast.success("Logo removed");
    } catch {
      toast.error("Could not remove logo");
    }
    setBusy(false);
  };
  return (
    <section className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="appearance-logo">
      <h2 className="text-sm font-bold">Header logo</h2>
      <p className="mt-0.5 text-xs text-zinc-500">Shown in the top-left of the admin and mobile app. Square images look best (PNG/JPG/HEIC).</p>
      <div className="mt-3 flex items-center gap-4">
        {logo ? (
          <img src={`${API}/public/header-logo?t=${bust}`} alt="Header logo" data-testid="appearance-logo-preview" className="h-16 w-16 rounded-lg border border-zinc-800 object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-zinc-700 text-[10px] uppercase tracking-widest text-zinc-500">None</div>
        )}
        <label data-testid="appearance-logo-upload" className={`inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border border-zinc-700 px-3 text-xs font-semibold text-zinc-200 hover:border-indigo-400 ${busy ? "pointer-events-none opacity-50" : ""}`}>
          <UploadSimple size={14} weight="bold" /> {logo ? "Replace logo" : "Upload logo"}
          <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={upload} data-testid="appearance-logo-input" />
        </label>
        {logo && (
          <button type="button" data-testid="appearance-logo-remove" onClick={remove} disabled={busy} className="inline-flex h-10 items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-red-300">
            <Trash size={14} weight="bold" /> Remove
          </button>
        )}
      </div>
    </section>
  );
};


const FaviconCard = () => {
  const [favicon, setFavicon] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  useEffect(() => {
    api.get("/settings/branding").then(({ data }) => setFavicon(data.favicon || null)).catch(() => {});
  }, []);
  const refreshTab = () => {
    const el = document.getElementById("app-favicon");
    if (el) el.href = `${API}/public/favicon?t=${Date.now()}`;
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/settings/favicon", fd);
      setFavicon(data.favicon);
      setBust(Date.now());
      refreshTab();
      toast.success("Favicon updated — browser tabs refresh on next load");
    } catch {
      toast.error("Upload failed");
    }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/settings/favicon");
      setFavicon(null);
      setBust(Date.now());
      toast.success("Favicon removed");
    } catch {
      toast.error("Could not remove favicon");
    }
    setBusy(false);
  };
  return (
    <section className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="appearance-favicon">
      <h2 className="text-sm font-bold">Browser favicon</h2>
      <p className="mt-0.5 text-xs text-zinc-500">The small icon shown in browser tabs and Google search results. Use a square PNG or ICO, at least 48×48.</p>
      <div className="mt-3 flex items-center gap-4">
        {favicon ? (
          <img src={`${API}/public/favicon?t=${bust}`} alt="Favicon" data-testid="appearance-favicon-preview" className="h-12 w-12 rounded-md border border-zinc-800 bg-zinc-950 object-contain p-1" />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-md border border-dashed border-zinc-700 text-[10px] uppercase tracking-widest text-zinc-500">None</div>
        )}
        <label data-testid="appearance-favicon-upload" className={`inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border border-zinc-700 px-3 text-xs font-semibold text-zinc-200 hover:border-[#c39e6d]/60 ${busy ? "pointer-events-none opacity-50" : ""}`}>
          <UploadSimple size={14} weight="bold" /> {favicon ? "Replace favicon" : "Upload favicon"}
          <input type="file" accept="image/png,image/x-icon,image/vnd.microsoft.icon,.ico,.png" className="hidden" onChange={upload} data-testid="appearance-favicon-input" />
        </label>
        {favicon && (
          <button type="button" data-testid="appearance-favicon-remove" onClick={remove} disabled={busy} className="inline-flex h-10 items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-red-300">
            <Trash size={14} weight="bold" /> Remove
          </button>
        )}
      </div>
      <p className="mt-3 text-[11px] leading-relaxed text-zinc-500">
        Google refreshes the search-result favicon the next time it crawls your public home page (can take days to weeks). The site must be publicly reachable and the home page must allow crawling.
      </p>
    </section>
  );
};


const ShopThemeCard = ({ theme, accent, gold, appx }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings/branding").then(({ data }) => setInfo(data.shop_theme || null)).catch(() => {});
  }, []);
  const push = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/shop-theme", { theme, accent, gold, appearance: appx, enforce: true });
      setInfo(data.shop_theme);
      localStorage.setItem("bg_shop_theme_at", data.shop_theme.pushed_at);
      toast.success("Theme pushed — every staff device picks it up on its next page load");
    } catch (err) {
      toast.error(err.response?.status === 403 ? "Only admins can push a shop-wide theme" : "Could not push theme");
    }
    setBusy(false);
  };
  const clear = async () => {
    setBusy(true);
    try {
      await api.delete("/settings/shop-theme");
      setInfo(null);
      toast.success("Shop-wide theme cleared — devices keep their own settings");
    } catch {
      toast.error("Could not clear");
    }
    setBusy(false);
  };
  return (
    <section className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="appearance-shop-theme">
      <h2 className="text-sm font-bold">Shop-wide theme</h2>
      <p className="mt-0.5 text-xs text-zinc-500">
        Push the theme, accent and colors you see right now to every staff device. Applied once per push; staff can still tweak locally afterwards.
      </p>
      {info?.pushed_at && (
        <p className="mt-2 text-[11px] text-zinc-400" data-testid="appearance-shop-theme-info">
          Last pushed {new Date(info.pushed_at).toLocaleString()} by {info.pushed_by}
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <button type="button" data-testid="appearance-shop-theme-push" onClick={push} disabled={busy} className="h-10 rounded-md bg-indigo-500 px-4 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400 disabled:opacity-50">
          Push to all devices
        </button>
        {info && (
          <button type="button" data-testid="appearance-shop-theme-clear" onClick={clear} disabled={busy} className="h-10 rounded-md border border-zinc-700 px-4 text-xs font-semibold text-zinc-300 hover:text-white">
            Stop enforcing
          </button>
        )}
      </div>
    </section>
  );
};

const ALPHA_PRESETS = [
  { key: "solid", label: "Solid", v: 1 },
  { key: "frosted", label: "Frosted", v: 0.9 },
  { key: "glass", label: "Glass", v: 0.8 },
  { key: "clear", label: "Clear", v: 0.68 },
];

export default function AppearanceSettings() {
  const navigate = useNavigate();
  const [theme, setThemeState] = useState(getTheme());
  const [accent, setAccent] = useState(getAccent());
  const [gold, setGold] = useState(getGold());
  const [appx, setAppx] = useState(getAppearance());
  const light = theme === "light";

  const setAcc = (k) => {
    setAccent(k);
    applyAccent(k);
  };
  const setGoldColor = (hex) => {
    setGold(hex);
    applyGold(hex);
  };
  const resetGoldColor = () => {
    resetGold();
    setGold(DEFAULT_GOLD);
  };
  const toggleTheme = () => {
    const next = light ? "dark" : "light";
    setThemeState(next);
    applyTheme(next);
  };
  const setField = (key, val) => {
    const next = { ...appx, [key]: val };
    setAppx(next);
    applyAppearance(next);
  };
  const setAlpha = (v) => setField("alpha", v);
  const reset = () => {
    resetAppearance();
    setAppx({ ...DEFAULT_APPEARANCE });
  };

  return (
    <div
      className="min-h-screen bg-zinc-950 text-zinc-100"
      data-testid="appearance-settings"
    >
      <AppHeader />
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <button
          type="button"
          onClick={() => navigate("/settings")}
          data-testid="appearance-back"
          className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-500 transition-colors duration-150 hover:text-zinc-200"
        >
          <ArrowLeft size={14} weight="bold" /> Settings
        </button>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Appearance
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Tune how the admin looks on this device. Your choice is saved in this
          browser — no redeploy needed.
        </p>

        {/* Theme presets */}
        <section className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="appearance-presets">
          <h2 className="text-sm font-bold">Themes</h2>
          <p className="mt-0.5 text-xs text-zinc-500">Pick a ready-made palette, then fine-tune the colors below.</p>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {PRESETS.map((t) => {
              const on = ["canvas", "sidebar", "panel", "ink"].every((k) => (appx[k] || "").toLowerCase() === t[k]);
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`appearance-preset-${t.key}`}
                  onClick={() => {
                    const next = { ...appx, canvas: t.canvas, sidebar: t.sidebar, panel: t.panel, ink: t.ink };
                    setAppx(next);
                    applyAppearance(next);
                    if (light) toggleTheme();
                  }}
                  className={`rounded-lg border p-2 text-left transition-colors ${on ? "border-indigo-400" : "border-zinc-800 hover:border-zinc-600"}`}
                >
                  <div className="flex h-10 overflow-hidden rounded-md" style={{ background: t.canvas }}>
                    <div className="w-1/4" style={{ background: t.sidebar }} />
                    <div className="m-1.5 flex-1 rounded-sm" style={{ background: t.panel }}>
                      <div className="m-1.5 h-1.5 w-2/3 rounded-full" style={{ background: t.ink }} />
                    </div>
                  </div>
                  <div className="mt-1.5 text-xs font-semibold text-zinc-200">{t.label}</div>
                </button>
              );
            })}
          </div>
        </section>

        <BrandLogoCard />
        <FaviconCard />

        <ShopThemeCard theme={theme} accent={accent} gold={gold} appx={appx} />

        {/* Room */}
        <section className="mt-8 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-sm font-bold">Room</h2>
              <p className="mt-0.5 text-xs text-zinc-500">
                Dark navy or a light workspace
              </p>
            </div>
            <button
              type="button"
              onClick={toggleTheme}
              data-testid="admin-theme-toggle"
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 px-4 py-2 text-xs font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:bg-zinc-800"
            >
              {light ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
              {light ? "Light" : "Dark"}
            </button>
          </div>
        </section>

        {/* Surface colors */}
        <section className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-bold">Surfaces &amp; colors</h2>
              <p className="mt-0.5 text-xs text-zinc-500">
                Pick your own colors for the page, the side menu, the panels and
                the text.
              </p>
            </div>
            <button
              type="button"
              onClick={reset}
              data-testid="appearance-reset"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              <ArrowCounterClockwise size={13} weight="bold" /> Reset
            </button>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {COLOR_FIELDS.map((f) => (
              <label
                key={f.key}
                className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
              >
                <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-zinc-700">
                  <input
                    type="color"
                    value={appx[f.key]}
                    onChange={(e) => setField(f.key, e.target.value)}
                    data-testid={`appearance-color-${f.key}`}
                    className="absolute -inset-2 h-[calc(100%+16px)] w-[calc(100%+16px)] cursor-pointer border-0 bg-transparent p-0"
                  />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-bold">{f.label}</span>
                  <span className="block truncate text-[11px] text-zinc-500">
                    {f.hint}
                  </span>
                  <span
                    className="mt-0.5 block font-mono text-[10px] uppercase text-zinc-400"
                    data-testid={`appearance-value-${f.key}`}
                  >
                    {appx[f.key]}
                    {(f.key === "ink" || f.key === "muted") && (() => {
                      const ratio = contrastRatio(appx[f.key] || "#ffffff", appx.panel || "#000000");
                      const tone = ratio >= 7 ? "text-emerald-300" : ratio >= 4.5 ? "text-amber-300" : "text-red-300";
                      const word = ratio >= 7 ? "Excellent" : ratio >= 4.5 ? "Readable" : "Hard to read";
                      return (
                        <span data-testid={`appearance-contrast-${f.key}`} className={`ml-2 normal-case ${tone}`}>
                          · {ratio.toFixed(1)}:1 {word}
                        </span>
                      );
                    })()}
                  </span>
                </span>
              </label>
            ))}
          </div>

          {/* Transparency */}
          <div className="mt-5">
            <h3 className="text-xs font-bold">Panel transparency</h3>
            <p className="mt-0.5 text-xs text-zinc-500">
              How much the canvas shows through your panels.
            </p>
            <div
              className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4"
              data-testid="appearance-alpha-group"
            >
              {ALPHA_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setAlpha(p.v)}
                  data-testid={`appearance-alpha-${p.key}`}
                  className={`rounded-lg border px-2 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    Math.abs((appx.alpha ?? 1) - p.v) < 0.001
                      ? "border-indigo-400 bg-indigo-500/15 text-white"
                      : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Live preview */}
          <div
            className="mt-5 overflow-hidden rounded-lg border border-zinc-800"
            data-testid="appearance-preview"
            style={{ background: appx.canvas }}
          >
            <div className="flex h-32">
              <div
                className="flex w-16 shrink-0 flex-col gap-1.5 p-2"
                style={{ background: appx.sidebar }}
              >
                <span className="h-2 w-full rounded-full bg-white/25" />
                <span className="h-2 w-3/4 rounded-full bg-white/15" />
                <span className="h-2 w-2/3 rounded-full bg-white/15" />
              </div>
              <div className="flex flex-1 items-center justify-center p-4">
                <div
                  className="w-full rounded-lg p-4"
                  style={{
                    background: hexAlpha(appx.panel, appx.alpha ?? 1),
                    backdropFilter: (appx.alpha ?? 1) < 0.96 ? "blur(7px)" : "none",
                    color: appx.ink,
                  }}
                >
                  <div className="text-xs font-bold">Panel preview</div>
                  <div className="mt-1 text-[11px] opacity-70">
                    Your text color on your panel color.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Typeface */}
        <section className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <h2 className="text-sm font-bold">Typeface</h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            The font used across the admin.
          </p>
          <div
            className="mt-4 grid gap-2 sm:grid-cols-3"
            data-testid="appearance-fonts"
          >
            {FONT_OPTIONS.map((f) => (
              <button
                key={f.key}
                type="button"
                data-testid={`appearance-font-${f.key}`}
                onClick={() => setField("font", f.key)}
                style={{ fontFamily: f.stack }}
                className={`rounded-lg border px-4 py-3 text-left transition-colors duration-150 ${
                  (appx.font || "default") === f.key
                    ? "border-indigo-400 bg-indigo-500/15 text-white"
                    : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                }`}
              >
                <span className="block text-sm font-semibold">{f.label}</span>
                <span className="block text-xs opacity-70">Aa Bb Cc 123</span>
              </button>
            ))}
          </div>
        </section>

        {/* Accent */}
        <section className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <h2 className="text-sm font-bold">Accent color</h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            Recolors buttons, highlights and active menus to match your brand.
          </p>
          <div
            className="mt-4 flex flex-wrap gap-3"
            data-testid="admin-accent-swatches"
          >
            {ACCENT_OPTIONS.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => setAcc(a.key)}
                data-testid={`admin-accent-${a.key}`}
                title={a.label}
                className={`flex h-11 w-11 items-center justify-center rounded-full border-2 transition-transform duration-150 hover:scale-110 ${
                  accent === a.key ? "border-white" : "border-transparent"
                }`}
                style={{ background: a.hex }}
              >
                {accent === a.key && (
                  <Check size={16} weight="bold" className="text-white" />
                )}
              </button>
            ))}
          </div>
        </section>

        {/* Highlight (gold) color */}
        <section className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="appearance-gold">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-bold">Highlight color</h2>
              <p className="mt-0.5 text-xs text-zinc-500">
                Recolors every gold/yellow highlight — money balances, reminder buttons,
                active tabs and status badges. Set this on its own, separate from the accent.
              </p>
            </div>
            <button
              type="button"
              onClick={resetGoldColor}
              data-testid="appearance-gold-reset"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              <ArrowCounterClockwise size={13} weight="bold" /> Reset
            </button>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="admin-gold-swatches">
            {GOLD_PRESETS.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => setGoldColor(g.hex)}
                data-testid={`admin-gold-${g.key}`}
                title={g.label}
                className={`flex h-11 w-11 items-center justify-center rounded-full border-2 transition-transform duration-150 hover:scale-110 ${
                  (gold || "").toLowerCase() === g.hex.toLowerCase() ? "border-white" : "border-transparent"
                }`}
                style={{ background: g.hex }}
              >
                {(gold || "").toLowerCase() === g.hex.toLowerCase() && (
                  <Check size={16} weight="bold" className="text-black/70" />
                )}
              </button>
            ))}
            <label
              className="flex items-center gap-2 rounded-lg border border-zinc-700 px-3 py-2 text-xs font-semibold text-zinc-300"
              title="Pick any custom highlight color"
            >
              <span className="relative h-6 w-6 shrink-0 overflow-hidden rounded-full border border-zinc-600">
                <input
                  type="color"
                  value={gold || DEFAULT_GOLD}
                  onChange={(e) => setGoldColor(e.target.value)}
                  data-testid="admin-gold-custom"
                  className="absolute -inset-2 h-[calc(100%+16px)] w-[calc(100%+16px)] cursor-pointer border-0 bg-transparent p-0"
                />
              </span>
              Custom
              <span className="font-mono text-[10px] uppercase text-zinc-500" data-testid="admin-gold-value">{gold}</span>
            </label>
          </div>
        </section>
      </main>
    </div>
  );
}

// hex + alpha → rgba() for the live preview panel
function hexAlpha(hex, a) {
  const h = String(hex || "").replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const int = parseInt(n, 16);
  if (Number.isNaN(int) || n.length !== 6) return hex;
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${a})`;
}
